import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import UIRadioButton from "@/components/ui-controls/radio-button";
import UITextField from "@/components/ui-controls/textfield";
import UISelect from "@/components/ui-controls/select";
import { useEffect, useState } from "react";

const LocationFields = ({ onChange, form = {}, disabled, predefinedLocations = [], appointmentLocations = [] }) => {
  const predefinedLocationsList = appointmentLocations.filter((loc) => loc.type !== 3);
  const hasPredefined = predefinedLocationsList.length > 0;
  const hasCustom = appointmentLocations.some((loc) => loc.type === 3);
  const locationsEmpty = !appointmentLocations || appointmentLocations.length === 0;

  const locationsKey = (appointmentLocations || [])
    .map((loc) => `${loc.calendarLocationId || loc.id}-${loc.type}-${loc.value}`)
    .join(",");
  const [lastLocationsKey, setLastLocationsKey] = useState(locationsKey);

  const [localPredefinedValue, setLocalPredefinedValue] = useState("");
  const [isPredefinedMode, setIsPredefinedMode] = useState(false);

  // Auto-select only when a single location type category is available
  useEffect(() => {
    if (locationsEmpty) return;
    const currentType = form?.values?.calendarLocationId;

    if (!hasCustom && currentType === 3 && hasPredefined) {
      if (predefinedLocationsList.length === 1) {
        const onlyPredefined = predefinedLocationsList[0];
        onChange("calendarLocationId", onlyPredefined.calendarLocationId || onlyPredefined.id);
        onChange("customLocation", onlyPredefined.value);
      } else {
        onChange("calendarLocationId", "");
        onChange("customLocation", null);
      }
    }
    if (!hasPredefined && currentType !== 3 && hasCustom) {
      onChange("calendarLocationId", 3);
      onChange("customLocation", "");
    }
  }, [
    appointmentLocations,
    hasPredefined,
    hasCustom,
    locationsEmpty,
    form?.values?.calendarLocationId,
    onChange,
    predefinedLocationsList,
  ]);

  useEffect(() => {
    if (locationsKey !== lastLocationsKey) {
      setLastLocationsKey(locationsKey);
      setIsPredefinedMode(false);
      return;
    }

    const id = form?.values?.calendarLocationId;

    if (id === 3) {
      setIsPredefinedMode(false);
      return;
    }

    if (id !== "" && id !== null && id !== undefined) {
      setIsPredefinedMode(true);
      return;
    }

    // Only one meeting-location type available — auto-select that mode
    if (hasPredefined && !hasCustom) {
      setIsPredefinedMode(true);
      return;
    }

    if (!hasPredefined && hasCustom) {
      setIsPredefinedMode(false);
      return;
    }
  }, [form?.values?.calendarLocationId, locationsKey, lastLocationsKey, hasPredefined, hasCustom]);

  const handleRadioChange = (isCustom) => {
    const isCurrentlyCustom = form?.values?.calendarLocationId === 3;
    if (isCustom && isCurrentlyCustom) return;
    if (!isCustom && isPredefinedMode) return;

    if (isCustom) {
      setIsPredefinedMode(false);
      onChange("calendarLocationId", 3);
      onChange("customLocation", "");
    } else {
      setIsPredefinedMode(true);
      if (predefinedLocationsList.length === 1) {
        const loc = predefinedLocationsList[0];
        onChange("calendarLocationId", loc.calendarLocationId || loc.id);
        onChange("customLocation", null);
        setLocalPredefinedValue(loc.value);
      } else {
        onChange("calendarLocationId", "");
        onChange("customLocation", null);
        setLocalPredefinedValue("");
      }
    }
  };

  const handleDropdownChange = (val) => {
    setLocalPredefinedValue(val);
    const loc = appointmentLocations.find((l) => l.value === val && l.type !== 3);
    if (loc) {
      onChange("calendarLocationId", loc.calendarLocationId || loc.id);
      onChange("customLocation", null);
    }
  };

  // Sync local predefined value when form values change externally (e.g. edit mode)
  useEffect(() => {
    if (form?.values?.calendarLocationId != 3) {
       const loc = appointmentLocations.find(l => (l.calendarLocationId || l.id) == form?.values?.calendarLocationId);
       if (loc) {
         setLocalPredefinedValue(loc.value);
       } else {
         setLocalPredefinedValue("");
       }
    }
  }, [form?.values?.calendarLocationId, appointmentLocations]);

  const isCustomSelected = form?.values?.calendarLocationId === 3;

  return (
    <>
      <LocationRadio
        isCustomSelected={isCustomSelected}
        isPredefinedSelected={isPredefinedMode}
        onChange={handleRadioChange}
        disabled={disabled}
        hasPredefined={hasPredefined}
        hasCustom={hasCustom}
        locationsEmpty={locationsEmpty}
      />

      {form?.touched?.customLocation && form?.errors?.customLocation && !isPredefinedMode && !isCustomSelected && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
          {form.errors.customLocation}
        </Typography>
      )}

      <Box sx={{ minWidth: 0, paddingTop: "5px", marginTop: "-5px", width: "100%", overflow: "hidden" }}>
        {isPredefinedMode && !isCustomSelected && (
          <UISelect
            name={"customLocation"}
            value={localPredefinedValue}
            options={predefinedLocations}
            title={"Location"}
            placeholder={"Choose a location"}
            onChange={(e) => handleDropdownChange(e.target.value)}
            disabled={disabled}
            clearable={false}
            error={
              (form.touched["customLocation"] &&
                form.errors["customLocation"]) ||
              false
            }
          />
        )}

        {isCustomSelected && (
          <UITextField
            type={"text"}
            name={"customLocation"}
            value={form.values.customLocation}
            title={"Custom Location"}
            placeholder={""}
            onChange={form.handleChange}
            onBlur={form?.handleBlur}
            error={
              (form.touched["customLocation"] &&
                form.errors["customLocation"]) ||
              false
            }
            disabled={disabled}
          />
        )}
      </Box>
    </>
  );
};

const LocationRadio = ({ isCustomSelected, isPredefinedSelected, onChange, disabled, hasPredefined, hasCustom, locationsEmpty }) => {
  const options = [
    {
      id: 1,
      isCustom: false,
      title: "Choose a Location",
      description: "Select Location",
      isDisabled: disabled || locationsEmpty || !hasPredefined,
      selected: isPredefinedSelected
    },
    {
      id: 2,
      isCustom: true,
      title: "Custom",
      description: "set custom location",
      isDisabled: disabled || locationsEmpty || !hasCustom,
      selected: isCustomSelected
    },
  ];

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
      {options.map((opt) => (
        <Box
          key={opt.id}
          onClick={() => !opt.isDisabled && onChange(opt.isCustom)}
          sx={{
            cursor: opt.isDisabled ? "default" : "pointer",
            border: "1px solid",
            borderColor: opt.selected ? "var(--palette-primary-main)" : "var(--palette-divider)",
            borderRadius: "8px",
            p: 1.5,
            backgroundColor: opt.selected ? "var(--palette-action-selected)" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.2s",
            opacity: opt.isDisabled ? 0.5 : 1,
            "&:hover": {
              borderColor: opt.isDisabled ? "var(--palette-divider)" : "var(--palette-primary-main)",
            },
            flex: 1
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "0.85rem",
                color: opt.selected ? "var(--palette-primary-main)" : "var(--palette-text-primary)",
              }}
            >
              {opt.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.7rem",
                color: "var(--palette-text-secondary)",
                lineHeight: 1.2
              }}
            >
              {opt.description}
            </Typography>
          </Box>
          <UIRadioButton
            checked={opt.selected}
            disabled={opt.isDisabled}
            name={"meetingLocationType"}
            style={{ marginRight: '-10px' }}
            handleChange={() => !opt.isDisabled && onChange(opt.isCustom)}
          />
        </Box>
      ))}
    </Box>
  );
};

export default LocationFields;
